import { makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    DEFAULT_HIGHLIGHT_COLOR,
    DEFAULT_HIGHLIGHT_COLOR_2,
    DEFAULT_HIGHLIGHT_COLOR_3,
    DEFAULT_HIGHLIGHT_COLOR_4,
    DEFAULT_HIGHLIGHT_COLOR_5,
    DEFAULT_HIGHLIGHT_COLOR_6,
    DEFAULT_HIGHLIGHT_COLOR_7,
    RulesType,
    ConfigType,
} from 'src/domains/sportsbook/trading/view/bet-ticker/BetTickerBox/config';
import { CompetitionPickerState } from './CompetitionPicker/CompetitionPicker.state';
import { deserializeNodeValue } from './CompetitionPicker/utils';
import { isNumeric } from './BetTickerForm.validators';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { CompetitionStoreState } from './CompetitionPicker/CompetitionStore.state';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { SportsListType } from 'src/AppState/sdk/staff';

export type BetTickerFormType = {
    filterBetsAbove: string;
    filterStakeFactor: string;
    filterStakeFactorCondition: string;
    highlightBetsAbove: string;
    highlightStakeFactor: string;
    highlightStakeFactorCondition: string;
    highlightPercentageMaxStakeAbove: string;
};

export class BetTickerFormState {
    public readonly filterBetsAbove: FormInputState<string, string>;
    public readonly filterStakeFactor: FormInputState<string, string>;
    public readonly filterStakeFactorCondition: FormInputState<string, string>;

    public readonly highlightBetsAbove: FormInputState<string, string>;
    public readonly highlightStakeFactor: FormInputState<string, string>;
    public readonly highlightStakeFactorCondition: FormInputState<string, string>;
    public readonly highlightPercentageMaxStakeAbove: FormInputState<string, string>;

    public readonly highlightBetsAboveColour: FormInputState<string, string>;
    public readonly highlightStakeFactorColour: FormInputState<string, string>;
    public readonly highlightPercentageMaxStakeAboveColour: FormInputState<string, string>;

    public readonly rejectedBets: FormInputState<boolean, boolean>;
    public readonly desktopOnlyBets: FormInputState<boolean, boolean>;
    public readonly mobileOnlyBets: FormInputState<boolean, boolean>;
    public readonly quickbetOnlyBets: FormInputState<boolean, boolean>;
    public readonly telebettingOnlyBets: FormInputState<boolean, boolean>;

    public readonly desktopOnlyColour: FormInputState<string, string>;
    public readonly mobileOnlyColour: FormInputState<string, string>;
    public readonly quickbetOnlyColour: FormInputState<string, string>;
    public readonly telebettingOnlyColour: FormInputState<string, string>;

    public readonly form: FormModel<BetTickerFormType>;

    public readonly competitionPickerState: CompetitionPickerState;

    public constructor(store: AutoMap<string, CompetitionStoreState>, sports: Resource<SportsListType>) {
        this.filterBetsAbove = FormInputState.new('').map(isNumeric(true, 'Field must be a number'));
        this.filterStakeFactor = FormInputState.new('').map(isNumeric(true, 'Field must be a number'));
        this.filterStakeFactorCondition = FormInputState.new('<=');
        this.highlightBetsAbove = FormInputState.new('').map(isNumeric(true, 'Field must be a number'));
        this.highlightStakeFactor = FormInputState.new('').map(isNumeric(true, 'Field must be a number'));
        this.highlightStakeFactorCondition = FormInputState.new('<=');
        this.highlightPercentageMaxStakeAbove = FormInputState.new('').map(isNumeric(true, 'Field must be a number'));
        this.highlightBetsAboveColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR);
        this.highlightStakeFactorColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_2);
        this.highlightPercentageMaxStakeAboveColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_7);
        this.rejectedBets = FormInputState.new(false);
        this.desktopOnlyBets = FormInputState.new(false);
        this.mobileOnlyBets = FormInputState.new(false);
        this.quickbetOnlyBets = FormInputState.new(false);
        this.telebettingOnlyBets = FormInputState.new(false);
        this.desktopOnlyColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_3);
        this.mobileOnlyColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_4);
        this.quickbetOnlyColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_5);
        this.telebettingOnlyColour = FormInputState.new(DEFAULT_HIGHLIGHT_COLOR_6);

        this.form = FormModel.group({
            filterBetsAbove: this.filterBetsAbove,
            filterStakeFactor: this.filterStakeFactor,
            filterStakeFactorCondition: this.filterStakeFactorCondition,
            highlightBetsAbove: this.highlightBetsAbove,
            highlightStakeFactor: this.highlightStakeFactor,
            highlightStakeFactorCondition: this.highlightStakeFactorCondition,
            highlightPercentageMaxStakeAbove: this.highlightPercentageMaxStakeAbove,
        });

        this.competitionPickerState = new CompetitionPickerState(store, sports);

        makeAutoObservable(this);
    }

    private get sports(): {
        id: string;
        competitions: boolean | { id: string; name: string | undefined }[];
    }[] {
        const competitionPicker = this.competitionPickerState.checked.map((e) => deserializeNodeValue(e));

        const sport: {
            id: string;
            competitions: boolean | { id: string; name: string | undefined }[];
        }[] = [];

        competitionPicker.forEach((item) => {
            if (item === null) return;

            const existingSport = sport.find((s) => s.id === item.sportId);

            let competitionLength = 0;
            const competitions = this.competitionPickerState.competitionStore.get(item.sportId).getCompetitions;
            if (competitions !== 'error' && competitions !== 'loading') {
                competitionLength = competitions.length;
            }

            if (item.type === 'sport') {
                if (existingSport === undefined) {
                    sport.push({ id: item.sportId, competitions: true });
                } else {
                    existingSport.competitions = true;
                }
            } else {
                if (existingSport === undefined) {
                    sport.push({ id: item.sportId, competitions: [{ id: item.competitionId, name: item.name }] });
                } else {
                    if (existingSport.competitions !== true) {
                        if (!Array.isArray(existingSport.competitions)) {
                            existingSport.competitions = [];
                        }
                        existingSport.competitions.push({ id: item.competitionId, name: item.name });

                        if (competitionLength === existingSport.competitions.length) {
                            existingSport.competitions = true;
                        }
                    }
                }
            }
        });

        return sport;
    }

    public get rules(): RulesType {
        return {
            ...(this.filterStakeFactorCondition.value.length > 0 && {
                stakeFactorCondition: this.filterStakeFactorCondition.value,
            }),
            ...(this.filterStakeFactor.value.length > 0 && { stakeFactor: parseFloat(this.filterStakeFactor.value) }),
            ...(this.filterBetsAbove.value.length > 0 && { betsAbove: parseFloat(this.filterBetsAbove.value) }),
            rejectedBets: this.rejectedBets.value,
            ...(this.sports.length > 0 && {
                sport: this.sports,
            }),
        };
    }

    public get config(): ConfigType {
        return {
            channelDesktop: this.desktopOnlyBets.value,
            channelMobile: this.mobileOnlyBets.value,
            channelQuickbet: this.quickbetOnlyBets.value,
            channelTelebetting: this.telebettingOnlyBets.value,
            desktopColour: this.desktopOnlyColour.value,
            mobileColour: this.mobileOnlyColour.value,
            quickbetColour: this.quickbetOnlyColour.value,
            telebettingColour: this.telebettingOnlyColour.value,
            highlightBetsAboveColour: this.highlightBetsAboveColour.value,
            highlightMaxStakePercentageColour: this.highlightPercentageMaxStakeAboveColour.value,
            highlightStakeFactorColour: this.highlightStakeFactorColour.value,
            highlightStakeFactorCondition: this.highlightStakeFactorCondition.value,
            ...(this.highlightStakeFactor.value.length > 0 && {
                highlightStakeFactor: parseFloat(this.highlightStakeFactor.value),
            }),
            ...(this.highlightPercentageMaxStakeAbove.value.length > 0 && {
                highlightMaxStakePercentage: parseFloat(this.highlightPercentageMaxStakeAbove.value),
            }),
            ...(this.highlightBetsAbove.value.length > 0 && {
                highlightBetsAbove: parseFloat(this.highlightBetsAbove.value),
            }),
        };
    }

    public isFormValid = (): boolean => {
        return this.form.result.value.type === 'ok';
    };
}
